import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  ComboboxOption,
  ComboboxStatic,
} from "@/components/fields/combobox/combobox-static";

// Re-export ComboboxOption for convenience
export type { ComboboxOption };

import { cn } from "@/lib/utils";

export type FormComboboxStaticProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  options: ComboboxOption[];
  label?: string;
  required?: boolean;
  placeholder?: string;
  description?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  descriptionClassName?: string;
  messageClassName?: string;
  onSelectItem?: (option: ComboboxOption | null) => void;
  onReload?: () => void;
  onLoading?: boolean;
  preserveMessageSpace?: boolean;
};

export const FormComboboxStatic = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  options,
  control,
  label,
  name,
  placeholder,
  description,
  wrapperClassName,
  labelClassName,
  inputClassName,
  descriptionClassName,
  messageClassName,
  onSelectItem,
  onReload,
  onLoading,
  preserveMessageSpace,
  ...props
}: FormComboboxStaticProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem
          className={wrapperClassName}
          aria-invalid={!!fieldState?.error}
        >
          {label && <FormLabel className={labelClassName}>{label}</FormLabel>}
          <FormControl>
            <ComboboxStatic
              options={options}
              buttonClassName={cn(
                "w-full",
                fieldState?.error &&
                  "aero:border-destructive aero:ring-destructive",
                inputClassName
              )}
              buttonPlaceholder={placeholder}
              {...field}
              value={field.value?.toString() ?? ""}
              onChange={(option) => {
                // Try to convert back to original type
                const originalValue = field.value;
                const newValue = option?.value;

                // If original was number, convert back to number
                if (typeof originalValue === "number" && newValue) {
                  field.onChange(Number(newValue));
                } else {
                  field.onChange(newValue);
                }

                if (onSelectItem) {
                  onSelectItem(option);
                }
              }}
              onReload={onReload}
              onLoading={onLoading}
            />
          </FormControl>
          {description && (
            <FormDescription className={descriptionClassName}>
              {description}
            </FormDescription>
          )}
          <div className={cn(preserveMessageSpace ? "aero:min-h-[18px]" : "")}>
            <FormMessage
              className={cn(
                fieldState?.error
                  ? "aero:text-destructive aero:text-xs"
                  : preserveMessageSpace
                    ? "aero:invisible aero:text-xs"
                    : "aero:hidden",
                messageClassName
              )}
            />
          </div>
        </FormItem>
      )}
      {...props}
    />
  );
};
